import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, TrendingUp, TrendingDown, DollarSign, Users, Heart, Sparkles } from 'lucide-react';
import { GameEvent } from '@/types/career-sim';

interface EventCardProps {
  event: GameEvent;
  onChoose: (choiceIndex: number) => void;
  onDismiss: () => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onChoose, onDismiss }) => {
  const [hoveredChoice, setHoveredChoice] = useState<number | null>(null);

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'opportunity': return '🎉';
      case 'challenge': return '⚠️';
      case 'random': return '🎲';
      default: return '📋';
    }
  };

  const getOutcomeIcon = (key: string) => {
    switch (key) {
      case 'funds': return <DollarSign className="w-3 h-3" />;
      case 'fanbase': return <Users className="w-3 h-3" />;
      case 'reputation': return <Sparkles className="w-3 h-3" />;
      case 'mentalHealth': return <Heart className="w-3 h-3" />;
      case 'energy': return <TrendingUp className="w-3 h-3" />;
      default: return null;
    }
  };

  const formatOutcomeValue = (key: string, value: number) => {
    const prefix = value > 0 ? '+' : '';
    switch (key) {
      case 'funds': return `${prefix}$${value}`;
      case 'fanbase': return `${prefix}${value} fans`;
      case 'reputation': return `${prefix}${value} rep`;
      case 'mentalHealth': return `${prefix}${value}% mental`;
      case 'energy': return `${prefix}${value}% energy`;
      case 'creativity': return `${prefix}${value}% creativity`;
      default: return `${prefix}${value}`;
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onDismiss}
    >
      <motion.div
        className="relative max-w-md w-full bg-gradient-to-br from-card via-card to-card/80 border-2 border-primary/30 rounded-xl shadow-2xl overflow-hidden"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 50 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 p-1 rounded-full bg-card/80 hover:bg-muted transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{getEventIcon(event.type)}</span>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">
                {event.type}
              </div>
              <h3 className="text-xl font-bold">{event.title}</h3>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="p-6 pb-4">
          <p className="text-foreground leading-relaxed">{event.description}</p>
        </div>

        {/* Choices */}
        <div className="p-6 pt-0 space-y-3">
          {event.choices.map((choice, index) => (
            <motion.button
              key={index}
              className="relative w-full p-4 text-left rounded-lg border-2 transition-all group"
              style={{
                borderColor: hoveredChoice === index ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                backgroundColor: hoveredChoice === index ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--card) / 0.5)'
              }}
              onClick={() => onChoose(index)}
              onMouseEnter={() => setHoveredChoice(index)}
              onMouseLeave={() => setHoveredChoice(null)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="font-semibold mb-2">{choice.text}</div>
              
              {/* Outcome Preview on Hover */}
              {hoveredChoice === index && choice.outcomes && (
                <motion.div
                  className="flex flex-wrap gap-2 mt-2"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.2 }}
                >
                  {Object.entries(choice.outcomes).map(([key, value]) => {
                    if (typeof value !== 'number') return null;
                    const isPositive = value > 0;
                    return (
                      <span
                        key={key}
                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                          isPositive 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}
                      >
                        {getOutcomeIcon(key)}
                        {formatOutcomeValue(key, value)}
                      </span>
                    );
                  })}
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EventCard;
